import { useObjectRef } from 'react-aria/useObjectRef';
import { Virtualizer } from 'react-aria/private/virtualizer/Virtualizer';
import React, {
  ComponentProps,
  createContext,
  ForwardedRef,
  ReactNode,
} from 'react';

const LoadingRendererContext = createContext<{
  renderLoadingSkeleton: (index: number) => ReactNode;
  loadingSkeletonRowsCount: number;
}>({
  loadingSkeletonRowsCount: NaN,
  renderLoadingSkeleton: () => {
    throw new Error('Illegal state.');
  },
});

type VirtualizerWithLoadingProps = Omit<
  ComponentProps<typeof Virtualizer>,
  'isLoading'
> & {
  loadingSkeletonRowsCount: number;
  renderLoadingSkeleton: (index: number) => ReactNode;
  emptyState?: ReactNode;
};

/**
 * Wraps Virtualizer from @react-aria/virtualizer to render skeleton loading rows for "loader" node,
 * in a way that it's reactive to `loadingSkeletonRowsCount` and  `renderLoadingSkeleton` props.
 * By default, the renderer function passed as Virtualizer children is not called in each re-render,
 * and a React context is used to make the rendered UI reactive to loading-related props.
 */
function _VirtualizerWithLoading(
  {
    renderLoadingSkeleton,
    loadingSkeletonRowsCount,
    emptyState,
    ...props
  }: VirtualizerWithLoadingProps,
  ref: ForwardedRef<HTMLDivElement>
) {
  return (
    <LoadingRendererContext.Provider
      value={{ renderLoadingSkeleton, loadingSkeletonRowsCount }}
    >
      <Virtualizer {...props} ref={useObjectRef(ref)}>
        {(kind, item) => {
          if (kind === 'loader') {
            return (
              <LoadingRendererContext.Consumer>
                {({ renderLoadingSkeleton, loadingSkeletonRowsCount }) =>
                  Array.from(Array(loadingSkeletonRowsCount)).map((_, index) =>
                    renderLoadingSkeleton(index)
                  )
                }
              </LoadingRendererContext.Consumer>
            );
          }
          if (!!emptyState && kind === 'empty-state') {
            return emptyState;
          }
          return <>{props.children(kind, item)}</>;
        }}
      </Virtualizer>
    </LoadingRendererContext.Provider>
  );
}

export const VirtualizerWithLoadingSkeleton = React.forwardRef(
  _VirtualizerWithLoading
) as (
  props: VirtualizerWithLoadingProps & {
    ref?: ForwardedRef<HTMLDivElement>;
  }
) => React.ReactElement;
